import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import { useConfig } from './Config';

const SearchContext = createContext<
  | {
      categories: string[];
      query: string;
      toggleCategory: (_category: string) => void;
      setQuery: (_query: string) => void;
      activeCategories: string[];
      categoriesWithIcons: string[];
      setCategoriesWithIcons: (_categories: string[]) => void;
    }
  | undefined
>(undefined);

type SearchProviderProps = {
  children: ReactNode;
};

function SearchProvider({ children }: SearchProviderProps) {
  const { searchCategories } = useConfig();

  const categories = useMemo(() => {
    return searchCategories
      .split(',')
      .map(raw => raw.trim())
      .filter(Boolean);
  }, [searchCategories]);
  const [query, setQuery] = useState('');
  const [categoriesWithIcons, setCategoriesWithIcons] = useState<string[]>([]);

  const toggleCategory = useCallback(
    (category: string) => {
      if (categories.includes(category)) {
        // Check if category is in search query
        if (query.toLowerCase().trim().includes(category.toLowerCase().trim())) {
          // strip category from query
          setQuery(query.replace(category, '').replace(/\s+/g, ' '));
        } else {
          // add category to query
          setQuery(`${query} ${category}`.trim());
        }
      }
    },
    [categories, query],
  );

  const activeCategories = useMemo(
    () => categories.filter(category => query.toLowerCase().includes(category.toLowerCase())),
    [categories, query],
  );

  const value = useMemo(
    () => ({
      categories,
      query,
      setQuery,
      toggleCategory,
      activeCategories,
      categoriesWithIcons,
      setCategoriesWithIcons,
    }),
    [categories, query, toggleCategory, activeCategories, categoriesWithIcons],
  );
  return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
}

function useSearch() {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error(`useSearch must be used within a SearchProvider`);
  }
  return context;
}

export { SearchProvider, useSearch };
